/**
 * Shared Stripe.js loader + minimal type surface.
 *
 * `useStripePaymentElement` (the unified Payment Element — card + ACH) loads
 * Stripe through this ONE module so a given publishable key resolves to a single
 * cached instance across flows — no double-load, no duplicate cache.
 *
 * We type-import narrowly (not from `@stripe/stripe-js`) so consumers don't need
 * that optional peer dependency for `tsc`.
 *
 * @module hooks/payment/stripeInstance
 */

// ─── Stripe minimal types ──────────────────────────────────────────────────

/**
 * The unified Payment Element (`elements.create('payment')`). Renders whatever
 * the Intent enables — card and/or `us_bank_account` (with Financial Connections
 * + the Nacha mandate rendered by Stripe). Mounted imperatively, like the Card
 * Element, so consumers don't need `@stripe/react-stripe-js`.
 */
export type StripePaymentElement = {
  mount(domElement: HTMLElement): void
  unmount(): void
  destroy(): void
  on(event: 'ready', handler: () => void): void
  on(event: 'loaderror', handler: (event: { error?: { message?: string } }) => void): void
  // Fires on every edit with the currently-selected method. `value.type` is Stripe's
  // payment_method_types value (e.g. 'card' | 'us_bank_account') — drives fee-per-method.
  // `complete` is true once the selected method's fields are filled and valid — it's
  // what gates the Save button (the Payment Element has no separate completeness event).
  on(
    event: 'change',
    handler: (event: { value?: { type?: string }; complete?: boolean }) => void,
  ): void
  off(event: string, handler: (...args: unknown[]) => void): void
}

export type StripeElements = {
  create(type: 'payment', options?: object): StripePaymentElement
}

export type StripeInstance = {
  /**
   * Create an Elements group for the Payment Element: pass the SetupIntent's
   * `{ clientSecret }` (the element renders that Intent's payment methods) plus
   * an optional `appearance`.
   */
  elements(options?: { clientSecret?: string; appearance?: object }): StripeElements
  /**
   * Confirm a SetupIntent using the Payment Element. Works for any method the
   * element rendered (card, `us_bank_account` → Financial Connections). `elements`
   * carries the `clientSecret` it was created with. `redirect: 'if_required'`
   * avoids a full-page redirect for on-page methods (ACH instant, most cards).
   */
  confirmSetup(options: {
    elements: StripeElements
    clientSecret?: string
    confirmParams?: { return_url?: string }
    redirect?: 'always' | 'if_required'
  }): Promise<{
    setupIntent?: { id: string; status: string }
    // `decline_code` carries the specific issuer reason on a `card_declined`
    // (e.g. `insufficient_funds`) — it's what `mapStripeCardError` keys branded
    // decline copy on, so it MUST be surfaced here.
    error?: { message?: string; code?: string; decline_code?: string; type?: string }
  }>
}

// ─── Module-level Stripe instance cache ───────────────────────────────────

/** Keyed by publishableKey → resolved Stripe instance (or null on failure). */
const stripeInstanceCache = new Map<string, StripeInstance | null>()
/** In-flight promises, keyed by publishableKey, to avoid double-loading. */
const stripeLoadingPromises = new Map<string, Promise<StripeInstance | null>>()

/**
 * Load (or return the cached) Stripe.js instance for a publishable key.
 *
 * Successful loads are cached; failures (ad-blocker, network) are NOT cached so
 * a later call can retry. In-flight loads are deduped via `stripeLoadingPromises`.
 */
export async function getStripeInstance(publishableKey: string): Promise<StripeInstance | null> {
  if (stripeInstanceCache.has(publishableKey)) {
    return stripeInstanceCache.get(publishableKey)!
  }

  if (stripeLoadingPromises.has(publishableKey)) {
    return stripeLoadingPromises.get(publishableKey)!
  }

  const promise = (async (): Promise<StripeInstance | null> => {
    try {
      // Dynamic import so @stripe/stripe-js is a true optional peer dependency
      const { loadStripe } = await import('@stripe/stripe-js')
      const instance = await loadStripe(publishableKey)
      const stripeInstance = instance as unknown as StripeInstance | null
      // Only cache successful loads — null means Stripe failed to initialize
      // (e.g. ad-blocker, network error), and we want future calls to retry.
      if (stripeInstance) {
        stripeInstanceCache.set(publishableKey, stripeInstance)
      }
      return stripeInstance
    } catch {
      // Don't cache failures — allow future calls to retry loading Stripe
      return null
    } finally {
      stripeLoadingPromises.delete(publishableKey)
    }
  })()

  stripeLoadingPromises.set(publishableKey, promise)
  return promise
}
